import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  UserGroupIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  ExclamationCircleIcon,
  LightBulbIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import Footer from './Footer';
import { oauthAPI } from '../services/api';
import { toast } from 'react-toastify';

const InstagramPersonalAccount = () => {
  const navigate = useNavigate();
  useTranslation(); // Initialize i18n for this page

  const handleConnectProfessional = async () => {
    try {
      const response = await oauthAPI.getInstagramAuthUrl('business');
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to start Instagram connection. Please try again.');
    }
  };

  const steps = [
    {
      number: 1,
      title: "Open Instagram App",
      description: "Open the Instagram app on your phone and go to your profile page.",
      icon: DevicePhoneMobileIcon,
    },
    {
      number: 2,
      title: "Go to Settings",
      description: "Tap the menu icon (☰) in the top right corner, then tap 'Settings and privacy'.",
      icon: null,
      detail: "Settings → Account type and tools",
    },
    {
      number: 3,
      title: "Switch to Professional",
      description: "Tap 'Switch to professional account' and follow the prompts.",
      icon: null,
      detail: "Select 'Business' or 'Creator' - either works perfectly with Vaulto!",
    },
    {
      number: 4,
      title: "Complete Setup",
      description: "Choose a category for your account and optionally add contact info. You can skip most fields.",
      icon: null,
      detail: "Tip: You can skip adding contact info if you prefer privacy",
    },
    {
      number: 5,
      title: "Come Back to Vaulto",
      description: "Once you've switched, return here and connect your Instagram account.",
      icon: SparklesIcon,
    },
  ];

  const benefits = [
    {
      icon: ChartBarIcon,
      title: "Free Instagram Insights",
      description: "See detailed analytics about your posts, reach, and engagement - completely free!",
    },
    {
      icon: UserGroupIcon,
      title: "Audience Demographics",
      description: "Learn about your followers - their location, age range, and when they're most active.",
    },
    {
      icon: ShieldCheckIcon,
      title: "Same Privacy Controls",
      description: "Keep your account private or public - you have the exact same options as before.",
    },
    {
      icon: SparklesIcon,
      title: "Access to Creator Tools",
      description: "Get access to features like contact buttons, quick replies, and scheduled posts.",
    },
  ];

  const faqs = [
    {
      question: "Will switching change how my profile looks?",
      answer: "Your profile will look almost identical! The only visible changes are a category label (which you can hide) and optional contact buttons. Your posts, followers, and everything else stays exactly the same.",
    },
    {
      question: "Can I keep my account private?",
      answer: "Yes! If you choose 'Creator' account type, you can absolutely keep your account private. Business accounts must be public, but Creator accounts have the same privacy options as personal accounts.",
    },
    {
      question: "Will I lose any followers or posts?",
      answer: "Absolutely not! Switching to a professional account doesn't affect your followers, posts, likes, comments, or any of your content. Everything stays exactly as it is.",
    },
    {
      question: "Can I switch back to personal if I don't like it?",
      answer: "Yes, you can switch back anytime! Just go to Settings → Account type and tools → Switch account type. However, if you switch back, you won't be able to use Vaulto's backup features.",
    },
    {
      question: "Why does Vaulto need a professional account?",
      answer: "Instagram's official API only allows apps like Vaulto to access content from professional accounts. This is Instagram's policy, not ours. It's actually a security feature that ensures only authorized apps can access your content.",
    },
    {
      question: "Is it really free to switch?",
      answer: "100% free! Instagram doesn't charge anything for professional accounts. They actually want more people to use professional features because it keeps creators on their platform.",
    },
    {
      question: "Do I need to connect to a Facebook Page?",
      answer: "If you choose 'Creator' account, no Facebook Page is required! For 'Business' accounts, you'll need to connect or create a Facebook Page (also free), but this is optional if you go with Creator.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 to-pink-900/50"></div>
      <div className="absolute top-0 left-0 right-0 h-full">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-32 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-10 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-gray-300 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Go Back
        </button>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full mb-6 shadow-lg shadow-pink-500/30">
            <LightBulbIcon className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-4">
            Switch to Professional Account
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
            It's <span className="text-green-400 font-semibold">free</span>, takes <span className="text-green-400 font-semibold">2 minutes</span>, and unlocks powerful features! Here's everything you need to know.
          </p>
        </div>

        {/* Why Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-white/20 shadow-2xl mb-8">
          <div className="flex items-center mb-6">
            <ExclamationCircleIcon className="h-8 w-8 text-yellow-400 mr-3" />
            <h2 className="text-2xl font-bold text-white">Why is this needed?</h2>
          </div>
          <p className="text-gray-300 text-lg leading-relaxed mb-4">
            Instagram requires apps like Vaulto to connect through their official API, which <span className="text-white font-medium">only works with Professional accounts</span> (Business or Creator). This is actually a <span className="text-green-400">good thing</span> - it's a security measure that ensures only authorized apps can access your content!
          </p>
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mt-4">
            <p className="text-green-300 flex items-start">
              <CheckCircleIcon className="h-5 w-5 mr-2 mt-1 flex-shrink-0" />
              <span>Don't worry - switching is completely free and you won't lose any followers, posts, or engagement. You can even switch back anytime!</span>
            </p>
          </div>
        </div>

        {/* Step-by-Step Guide */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-white/20 shadow-2xl mb-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            📱 Step-by-Step Guide
          </h2>
          <div className="space-y-6">
            {steps.map((step, index) => (
              <div key={step.number} className="relative">
                {index < steps.length - 1 && (
                  <div className="absolute left-6 top-14 w-0.5 h-full bg-gradient-to-b from-pink-500 to-purple-600 -z-10"></div>
                )}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {step.number}
                  </div>
                  <div className="flex-1 bg-white/5 rounded-2xl p-4 border border-white/10">
                    <h3 className="text-lg font-semibold text-white mb-1">{step.title}</h3>
                    <p className="text-gray-300">{step.description}</p>
                    {step.detail && (
                      <p className="text-sm text-pink-300 mt-2 font-medium">
                        💡 {step.detail}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="mt-10 text-center">
            <p className="text-gray-300 mb-4">Already switched to Professional?</p>
            <button
              onClick={handleConnectProfessional}
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-semibold rounded-2xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Connect My Instagram Now
              <ArrowRightIcon className="ml-3 h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-white/20 shadow-2xl mb-8">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            🎁 Free Benefits You'll Get
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-3">
                    <benefit.icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">{benefit.title}</h3>
                </div>
                <p className="text-gray-300 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-white/20 shadow-2xl mb-8">
          <div className="flex items-center justify-center mb-8">
            <QuestionMarkCircleIcon className="h-8 w-8 text-purple-400 mr-3" />
            <h2 className="text-2xl font-bold text-white">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-white/5 rounded-2xl border border-white/10 overflow-hidden"
              >
                <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-white/5 transition-colors">
                  <span className="text-white font-medium pr-4">{faq.question}</span>
                  <span className="flex-shrink-0 w-8 h-8 bg-white/10 rounded-full flex items-center justify-center group-open:rotate-180 transition-transform">
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </summary>
                <div className="px-5 pb-5 pt-0">
                  <p className="text-gray-300">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>

        {/* Recommendation Box */}
        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-purple-500/30 shadow-2xl mb-8">
          <div className="text-center">
            <h3 className="text-xl font-bold text-white mb-3">🌟 Our Recommendation</h3>
            <p className="text-gray-300 mb-4">
              Choose <span className="text-purple-300 font-semibold">"Creator"</span> account type - it gives you all the features you need for Vaulto, and you can keep your account <span className="text-green-400 font-semibold">private</span> if you want!
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6">
              <div className="bg-white/10 rounded-xl px-6 py-3 border border-white/20">
                <span className="text-gray-400">Business</span>
                <span className="mx-2 text-gray-500">•</span>
                <span className="text-gray-400">Must be public</span>
              </div>
              <div className="bg-purple-500/20 rounded-xl px-6 py-3 border border-purple-500/40">
                <span className="text-purple-300 font-medium">Creator</span>
                <span className="mx-2 text-gray-500">•</span>
                <span className="text-green-400">Can be private ✓</span>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center py-8">
          <h3 className="text-2xl font-bold text-white mb-4">Ready to protect your content?</h3>
          <p className="text-gray-300 mb-6">
            Switch to a Professional account on Instagram, then come back and connect!
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://help.instagram.com/502981923235522"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 border border-white/30 text-base font-medium rounded-xl text-white hover:bg-white/10 transition-all"
            >
              Instagram Help Guide
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </a>
            <button
              onClick={handleConnectProfessional}
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-semibold rounded-2xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              I've Switched - Connect Now!
              <ArrowRightIcon className="ml-3 h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Need Help */}
        <div className="text-center mt-8 pb-8">
          <p className="text-gray-400">
            Need help? Check out our{' '}
            <a href="/help" className="text-pink-400 hover:text-pink-300 underline">
              Help Center
            </a>{' '}
            or{' '}
            <a href="/contact" className="text-pink-400 hover:text-pink-300 underline">
              Contact Support
            </a>
          </p>
        </div>
      </div>

      <Footer variant="dark" />
    </div>
  );
};

export default InstagramPersonalAccount;
